import React, { forwardRef } from "react"
import PropTypes from "prop-types"
import { propsUtil } from "@asphalt-react/helper"
import cn from "classnames"

import styles from "./nav-link.module.css"
import { showIndicator } from "./helper.js"
import { DEFAULT_ACTIVE_STYLE } from "./values.js"

const { resolvePropCollision } = propsUtil

export const NavLink = forwardRef(
  (
    {
      children,
      as: Link = "a",
      asProps = {},
      active = false,
      prominent = false,
      highlight = false,
      icon = false,
      bezel = true,
      ...props
    },
    ref
  ) => {
    // filtering out style and className
    /* eslint-disable-next-line */
    const { style, className, ...rest } = props

    const { collision, value: activeStyle } = resolvePropCollision(
      { highlight, prominent },
      DEFAULT_ACTIVE_STYLE
    )

    if (collision) {
      console.warn(
        `NavLink: Multiple active styles detected. Falling back to "${DEFAULT_ACTIVE_STYLE}"`
      )
    }

    // filtering out className and style props from `asProps`
    const { style: asStyle, className: asClass, ...restProps } = asProps

    const classes = cn(
      styles.NavLink,
      { [styles[activeStyle]]: active },
      { [styles.bezelless]: !bezel }
    )

    const contentClasses = cn(
      styles.NavItemContent,
      styles[`${activeStyle}Content`],
      { [styles.bezelless]: !bezel },
      { [styles.icon]: icon }
    )

    return (
      <Link
        {...rest}
        {...restProps}
        className={classes}
        ref={ref}
        role="menuitem"
      >
        <span className={contentClasses} data-testid="nav-item-content">
          {children}
        </span>
        <span
          className={cn(styles.indicatorWrap, {
            [styles.showIndicator]: showIndicator(activeStyle),
          })}
        >
          <span
            className={cn(styles.indicator, { [styles.visible]: active })}
          ></span>
        </span>
      </Link>
    )
  }
)

NavLink.displayName = "NavLink"

NavLink.propTypes = {
  /**
   * React node to render link content.
   */
  children: PropTypes.node,
  /**
   * Html element/React component to replace the default element. Using this prop, you can use your router's link element, such as "react-router-dom"'s Link element.
   */
  as: PropTypes.elementType,
  /**
   * Pass the props such as "href", "id" for the custom link element passed in `as` prop.
   */
  asProps: PropTypes.object,
  /**
   * Marks the nav link as active.
   */
  active: PropTypes.bool,
  /**
   * An active style. Adds an indicator to accent the active nav link.
   */
  prominent: PropTypes.bool,
  /**
   * A subtle active style. Highlights the surface of the nav item.
   */
  highlight: PropTypes.bool,
  /**
   * An icon only nav link.
   */
  icon: PropTypes.bool,
  /**
   * Adds padding.
   */
  bezel: PropTypes.bool,
}

NavLink.defaultProps = {
  as: "a",
  asProps: {},
  active: false,
  prominent: false,
  highlight: false,
  bezel: true,
  icon: false,
}
